import {
  Button, Card, StatusPill, SegmentedControl, Ring, Sparkbars, ProgressBar,
  KpiCard, DarkHeader, Timeline, DataTable, Logo,
} from '@careandkey/shared';
import { useState } from 'react';

// Styleguide v3 — the validated direction: light field #F3F4F7 + dark chrome
// #0B0F17, blue accent #3B5BFD, white 16px cards, Geist + Geist Mono.
// Token-driven; the 4 mocked screens (docs/design/B-*.dc.html) will reuse these.

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="eyebrow">{title}</h2>
      {children}
    </section>
  );
}
function Swatch({ name, varName, dark }: { name: string; varName: string; dark?: boolean }) {
  return (
    <div className="space-y-1.5">
      <div className="h-12 rounded-md" style={{ background: `var(${varName})`, border: '1px solid var(--pv-border)' }} />
      <div className={dark ? 'font-mono text-2xs text-chrome-muted' : 'font-mono text-2xs text-muted'}>{name}</div>
    </div>
  );
}

export function Styleguide() {
  const [seg, setSeg] = useState(0);

  return (
    <div className="flex min-h-screen bg-bg text-fg">
      {/* dark sidebar (chrome) */}
      <aside className="hidden w-56 shrink-0 flex-col gap-1 bg-chrome p-4 text-chrome-fg md:flex">
        <div className="mb-4 px-2">
          <Logo variant="dark" size={26} />
        </div>
        {['Dashboard', 'Work orders', 'Assets', 'Properties', 'Due dates', 'Clients', 'Help'].map((n, i) => (
          <a
            key={n}
            className={
              'rounded-md px-3 py-2 text-sm ' +
              (i === 1 ? 'bg-chrome-3 text-chrome-fg' : 'text-chrome-muted hover:bg-chrome-2 hover:text-chrome-fg')
            }
          >
            {n}
          </a>
        ))}
      </aside>

      <main className="flex-1 space-y-8 overflow-x-hidden px-6 py-8">
        <div className="flex items-baseline justify-between">
          <h1 className="text-lg font-semibold">Styleguide</h1>
          <span className="font-mono text-2xs text-muted">tokens + components · validated direction</span>
        </div>

        {/* LOGO */}
        <Section title="Logo · variant B">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex flex-col items-center gap-2 rounded-card bg-chrome p-6">
              <Logo variant="dark" size={34} />
              <span className="font-mono text-2xs text-chrome-muted">dark · on chrome</span>
            </div>
            <div className="flex flex-col items-center gap-2 rounded-card border border-border bg-card p-6">
              <Logo variant="light" size={34} />
              <span className="font-mono text-2xs text-muted">light · on field</span>
            </div>
            <div className="flex items-center gap-4 rounded-card border border-border bg-card p-6">
              <Logo variant="dark" size={34} withWordmark={false} />
              <Logo variant="light" size={34} withWordmark={false} />
              <span className="font-mono text-2xs text-muted">symbol only</span>
            </div>
            <div className="flex items-end gap-4 rounded-card bg-chrome p-6">
              <Logo variant="dark" size={22} />
              <Logo variant="dark" size={34} />
              <Logo variant="dark" size={52} />
              <span className="font-mono text-2xs text-chrome-muted">sizes</span>
            </div>
          </div>
          <ul className="mt-3 space-y-1 text-sm text-muted">
            <li>· One word, no dot or separator: <span className="font-medium text-fg">PREVANT</span>IQ.</li>
            <li>· “PREVANT” Geist weight 300 at ≥24px, 400 below; “IQ” weight 700, tucked tight.</li>
            <li>· letter-spacing 0.14em; one colour only — #FFFFFF on dark chrome, #0B0F17 on light.</li>
            <li>· Symbol: rounded square (radius 10/34 of the side), bold “P”; invert fill per background.</li>
            <li>· Favicon, PWA and apple-touch icons are generated from the symbol alone (incl. maskable).</li>
          </ul>
        </Section>

        {/* COLOURS */}
        <Section title="Light field">
          <div className="grid grid-cols-4 gap-3 sm:grid-cols-6">
            <Swatch name="bg #F3F4F7" varName="--pv-bg" />
            <Swatch name="card" varName="--pv-card" />
            <Swatch name="fg" varName="--pv-fg" />
            <Swatch name="muted #667085" varName="--pv-muted" />
            <Swatch name="segmented" varName="--pv-segmented" />
            <Swatch name="accent #3B5BFD" varName="--pv-accent" />
          </div>
        </Section>
        <Section title="Dark chrome">
          <div className="grid grid-cols-4 gap-3 rounded-card bg-chrome p-4 sm:grid-cols-6">
            <Swatch name="chrome #0B0F17" varName="--pv-chrome" dark />
            <Swatch name="chrome-2" varName="--pv-chrome-2" dark />
            <Swatch name="chrome-3" varName="--pv-chrome-3" dark />
            <Swatch name="border" varName="--pv-chrome-border" dark />
            <Swatch name="fg" varName="--pv-chrome-fg" dark />
            <Swatch name="accent #5B7BFF" varName="--pv-accent-dark" dark />
          </div>
        </Section>

        {/* BUTTONS */}
        <Section title="Buttons">
          <div className="flex flex-wrap items-center gap-2.5">
            <Button>Primary (dark)</Button>
            <Button variant="accent">Accent</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="danger">Delete</Button>
            <div className="flex items-center gap-2.5 rounded-card bg-chrome p-3">
              <Button variant="accent" onDark>
                Accent
              </Button>
              <Button variant="secondary" onDark>
                Secondary
              </Button>
            </div>
          </div>
        </Section>

        {/* STATUS PILLS */}
        <Section title="Status pills (6px)">
          <div className="flex flex-wrap gap-2">
            <StatusPill tone="inprogress">In progress</StatusPill>
            <StatusPill tone="open">Open</StatusPill>
            <StatusPill tone="overdue">Overdue</StatusPill>
            <StatusPill tone="onhold">On hold</StatusPill>
            <StatusPill tone="done">Done</StatusPill>
          </div>
        </Section>

        {/* SEGMENTED */}
        <Section title="Segmented control">
          <SegmentedControl items={['Today', 'Week', 'Month']} active={seg} onChange={setSeg} />
        </Section>

        {/* CHARTS */}
        <Section title="Ring · Sparkbars · ProgressBar">
          <div className="flex flex-wrap items-center gap-8">
            <Ring value={95} />
            <Ring value={62} size={80} stroke={8} />
            <Sparkbars values={[4, 7, 5, 9, 6, 8, 11, 7, 10]} />
            <div className="w-48 space-y-2">
              <ProgressBar value={72} />
              <ProgressBar value={34} />
            </div>
          </div>
        </Section>

        {/* KPI CARDS */}
        <Section title="KpiCard — light + dark">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <KpiCard label="Compliance" value="95.2%" sub="Target 95%">
              <Ring value={95} size={48} stroke={5} label="" />
            </KpiCard>
            <KpiCard label="Open work orders" value="12">
              <Sparkbars values={[3, 5, 4, 6, 8, 5, 7]} />
            </KpiCard>
            <KpiCard label="Compliance" value="95.2%" sub="Target 95%" dark>
              <Ring value={95} size={48} stroke={5} onDark label="" />
            </KpiCard>
            <KpiCard label="Open work orders" value="12" dark>
              <Sparkbars values={[3, 5, 4, 6, 8, 5, 7]} color="var(--pv-accent-dark)" />
            </KpiCard>
          </div>
        </Section>

        {/* DARK HEADER */}
        <Section title="DarkHeader (detail pages)">
          <DarkHeader
            breadcrumb="Org · Client · Villa A · Kitchen"
            title="WO-2026-0042 · Fire extinguisher"
            meta={
              <>
                <StatusPill tone="inprogress">In progress</StatusPill>
                <span className="font-mono">a1b2c3d4e5</span>
                <span>· priority high</span>
              </>
            }
            actions={
              <>
                <Button variant="secondary" onDark>
                  Assign
                </Button>
                <Button onDark>Start</Button>
              </>
            }
          />
        </Section>

        {/* TIMELINE */}
        <Section title="Timeline">
          <Card className="p-5">
            <Timeline
              items={[
                { time: '09:12', title: 'Created', desc: 'Raised by owner (service request)' },
                { time: '09:40', title: 'Assigned', desc: 'Technician A' },
                { time: '10:05', title: 'En route', desc: 'GPS ping · 0.4 km' },
                { time: '10:22', title: 'Arrived', desc: 'Geofence confirmed' },
              ]}
            />
          </Card>
        </Section>

        {/* TABLE */}
        <Section title="List table">
          <Card className="px-2 py-1">
            <DataTable
              columns={['WO', 'Asset', 'Property', 'Priority', 'Due', 'Status']}
              rows={[
                ['WO-0042', 'Fire extinguisher', 'Villa A', 'High', '2026-11-01', <StatusPill tone="overdue">Overdue</StatusPill>],
                ['WO-0043', 'Pool pump', 'Villa A', 'Medium', '2027-02-15', <StatusPill tone="inprogress">In progress</StatusPill>],
                ['WO-0044', 'UPS battery', 'Hotel B', 'Low', '2026-09-20', <StatusPill tone="open">Open</StatusPill>],
              ]}
            />
          </Card>
        </Section>
      </main>
    </div>
  );
}
